import React, { useState } from "react";
import { useSelector } from "react-redux";
import dragIcon from "svg/drag-icon.svg";
import CustomTooltip from "common/tooltip";
import { useParams } from "react-router-dom";
import "../../style.scss";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { currencyValue, materialItemsHeadersEditable, materialItemsHeaders, calcuateLineItemTotal } from "../../utils";
import ProductEditModal from "../modals/ProductEditModal";
import Modal from "common/modal";
import deleteIcon from "svg/delete_icon.svg";
import { toast } from "react-toastify";
import { api } from "store/slices/api";

export const DraggableProductRow = ({ row }) => {
  const { quoteId, floorId } = useParams();
  const tdClass = "table-root-floor__body__tr__td table-root-floor__body__tr__td__text";
  const tdWidth = "table-root-floor__body__tr__td table-root-floor__body__tr__td__all-text";
  const { data: quote = {} } = api.useGetPriceQuoteQuery(quoteId);
  const [updateProduct] = api.useUpdateFloorProductsMutation();
  const [deleteProduct] = api.useDeleteFloorProductsMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { storeCcn, terrCcn } = useSelector((state) => state.loggedInUser);
  const canEdit =
    (quote.territory === terrCcn && quote.territory !== "800100") ||
    (storeCcn === quote.storeNumber && quote.punchoutPublished !== true);
  const headers = canEdit ? materialItemsHeadersEditable : materialItemsHeaders;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.rank,
  });

  const handleProductRowSelection = () => {
    const isTaxChecked = !row.isTaxChecked;
    const { productId, price, quantity } = row;
    const totals = calcuateLineItemTotal({ price, quantity, taxRate: isTaxChecked ? quote.taxRate : 0 });

    toast.promise(updateProduct({ ...totals, productId, floorId, isTaxChecked }), {
      pending: "Saving changes",
      success: "Products updated successfully",
      error: "Failed to update products",
    });
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const displayRowItem = (row, col) => {
    if (row && col && col.dataKey) {
      const value = row[col.dataKey];
      if (col.name && (col.name === "Extended Price" || col.name === "Sales Tax" || col.name === "Total")) {
        return value != null ? currencyValue(value) : "";
      }
      if (!value) return "";
      if (col.name && col.name === "Description") {
        return (
          <CustomTooltip placement="top" text={value}>
            {value}
          </CustomTooltip>
        );
      }
      return value;
    }
    return "";
  };

  return (
    <tr ref={setNodeRef} style={style} className="table-root-floor__body__tr">
      {headers.map((col) => {
        if (col.name === "Checkbox") {
          return (
            <td
              key={col.id}
              className="table-root-floor__body__tr__td table-root-floor__body__tr__td__checkbox product"
            >
              <input
                type="checkbox"
                aria-label="Checkbox selection of rows"
                className="table-root-floor__body__tr__td__checkbox--input"
                checked={row.isTaxChecked}
                onChange={() => handleProductRowSelection()}
                disabled={!canEdit}
              />
            </td>
          );
        }
        if (col.name === "Sales Number") {
          return (
            <td key={col.id} className={tdClass} style={{ minWidth: 125 }}>
              {canEdit && (
                <span {...attributes} {...listeners} style={{ cursor: "grab" }} aria-label="Drag to reorder">
                  <img src={dragIcon} alt="Drag" />
                </span>
              )}
              {!row.salesNumber ? "N/A" : row.salesNumber}
            </td>
          );
        }
        if (col.name === "Edit") {
          return <ProductEditModal key={col.id} {...row} />;
        }
        if (col.name === "Delete") {
          return (
            <td key={col.id} className={`${tdClass} actions-align`}>
              <img
                src={deleteIcon}
                className="actions-align"
                alt="delete-icon"
                onClick={() => setShowDeleteModal(true)}
              />
              {showDeleteModal && (
                <Modal
                  title="Delete"
                  saveButtonText="Delete"
                  closeButtonText="Cancel"
                  handleSave={() => {
                    setShowDeleteModal(false);
                    toast.promise(deleteProduct({ productId: row.productId, floorId }).unwrap(), {
                      pending: "Deleting product",
                      success: "Product deleted successfully",
                      error: "Failed to delete product",
                    });
                  }}
                  handleCancel={() => setShowDeleteModal(false)}
                  saveButtonClass="removeBtnClass"
                  rootClass="transparent-backdrop"
                  size="lg"
                  bodyClass="scroll-bar"
                >
                  <p className="confirmation-text">Are you sure you want to delete {row.productName} ?</p>
                </Modal>
              )}
            </td>
          );
        }
        if (
          col.name === "Unit Price" &&
          row.oldPrice !== null &&
          row.oldPrice !== "null" &&
          parseFloat(row.oldPrice).toFixed(2) !== parseFloat(row.price).toFixed(2) &&
          parseFloat(row.price).toFixed(2) !== parseFloat("0").toFixed(2)
        ) {
          return (
            <td
              key={col.id}
              className={`${tdClass} mobile-data__row__column__item__top__left__name--text customer-txt`}
            >
              <CustomTooltip placement="top" trigger="hover" text={`Original Price: ${currencyValue(row.oldPrice)}`}>
                <span>{currencyValue(row.price)}</span>
              </CustomTooltip>
            </td>
          );
        }
        if (col.name === "Unit Price" && parseFloat(row.price).toFixed(2) === parseFloat("0").toFixed(2)) {
          return (
            <td key={col.id} className={`${tdClass} mobile-data__row__column__item__top__left__name--text price-txt`}>
              <span>{currencyValue(row.price)}</span>
            </td>
          );
        }
        if (col.name === "Unit Price") {
          return (
            <td key={col.id} className={tdClass}>
              <span>{currencyValue(row.price)}</span>
            </td>
          );
        }
        if (col.name === "Product Name") {
          return (
            <td key={col.id} className={tdClass}>
              <CustomTooltip placement="top" trigger="hover" text={row.productName}>
                <span>{row.productName}</span>
              </CustomTooltip>
            </td>
          );
        }
        if (
          col.name !== "Checkbox" &&
          col.name !== "Unit Price" &&
          col.name !== "Edit" &&
          col.name !== "Delete" &&
          col.name !== "Sales Number" &&
          col.name !== "Product Name"
        ) {
          return (
            <td key={col.id} className={tdWidth}>
              {displayRowItem(row, col)}
            </td>
          );
        }
        return null;
      })}
    </tr>
  );
};
